import { Close } from '@mui/icons-material'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Slide,
  Button,
  TextField,
  IconButton,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import type { JSX } from 'react'
import React from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { updateDeviceUid } from '@/api/Device'
import type { Device } from '@/pages/Device/Device'
import { labelSx } from '@/styles/MuiComponentStyles'

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  device: Device | undefined
}

type FormValues = {
  serial: string
  newUid: string
}

const Transition = React.forwardRef(function Transition(
  props: React.ComponentProps<typeof Slide>,
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />
})

const UidChangeDialog = ({ device, open, setOpen }: Props): JSX.Element => {
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('common')

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { serial: '', newUid: '' },
  })

  const handleClose = () => {
    setOpen(false)
    reset()
  }

  const onSubmit = async (values: FormValues) => {
    try {
      const res = await updateDeviceUid(
        device?.uid,
        values.serial,
        values.newUid,
      )
      setOpen(false)
      reset()

      if (res.status === 200) {
        enqueueSnackbar(t('device.info.uidChange.uidChangeSuccess'), {
          variant: 'success',
        })
        navigate(`/v2/main/device/${values.newUid}/info`)
      } else if (res.status === 409) {
        enqueueSnackbar(t('device.info.uidChange.newUidAlreadyExistsError'), {
          variant: 'error',
        })
      } else {
        enqueueSnackbar(t('device.info.uidChange.uidChangeFailed'), {
          variant: 'error',
        })
      }
    } catch (error) {
      console.error('Uid change failed', error)
      enqueueSnackbar(t('device.info.uidChange.uidChangeFailed'), {
        variant: 'error',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      keepMounted
      aria-labelledby="uid-change-title"
      aria-describedby="uid-change-description"
      slots={{ transition: Transition }}
    >
      <DialogTitle
        id="uid-change-title"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ fontWeight: 'normal', fontSize: '1.0rem' }}>
          {t('general.areYouSure')}
        </span>
        <IconButton onClick={handleClose}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent id="uid-change-description">
        <div
          style={{
            fontWeight: 'normal',
            fontSize: '1.0rem',
            marginBottom: '16px',
          }}
        >
          {t('device.info.uidChange.header')}
        </div>

        <div>
          <div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Controller
                name="serial"
                control={control}
                rules={{
                  required: t('device.info.uidChange.serialIncorrectError'),
                  validate: (value) =>
                    value === device?.serial ||
                    t('device.info.uidChange.serialIncorrectError'),
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    label={t('device.info.uidChange.serialInputLabel')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: { sx: labelSx },
                      input: {
                        inputProps: {
                          inputMode: 'text',
                        },
                      },
                    }}
                    sx={{ mb: 2 }}
                  />
                )}
              />

              <Controller
                name="newUid"
                control={control}
                rules={{
                  required: t(
                    'device.info.uidChange.newUidInputLengthValidationError',
                  ),
                  validate: (value) => {
                    if (value.length !== 32) {
                      return t(
                        'device.info.uidChange.newUidInputLengthValidationError',
                      )
                    }
                    if (value === device?.uid) {
                      return t('device.info.uidChange.newUidSameError')
                    }
                    if (
                      value.startsWith('01') &&
                      String(device?.version) === '2'
                    ) {
                      return t('device.info.uidChange.v2ToV2UIdChangeError')
                    }
                    return true
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    variant="standard"
                    fullWidth
                    label={t('device.info.uidChange.newUidInputLabel')}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      inputLabel: { sx: labelSx },
                      input: {
                        inputProps: {
                          pattern: '^[a-z0-9]{32}$',
                          maxLength: 32,
                          inputMode: 'text',
                        },
                      },
                    }}
                    onInput={(e) => {
                      const input = e.target as HTMLInputElement
                      input.value = input.value
                        .replaceAll(/[^a-z0-9]/g, '')
                        .slice(0, 32)
                      field.onChange(input.value)
                    }}
                  />
                )}
              />

              <DialogActions sx={{ justifyContent: 'center', p: 2 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    reset()
                    handleClose()
                  }}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="success"
                  disabled={!isValid}
                >
                  {t('general.yes')}
                </Button>
              </DialogActions>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default UidChangeDialog
